import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '@env/environment';

import { ApiService } from '@app/core/http/api.service';

import { Observable ,  forkJoin } from 'rxjs';
import { map, flatMap } from 'rxjs/operators';

import {
    IOcbNotificationGroup,
    IOcbCUNotificationGroup,
    IOcbEnvironmentRecipient,
    IOcbCUEnvironmentRecipient,
    OcbFileTransferLogTypes,
    OcbEnvironmentLogTypes,
    OcbRemotePlatformLogTypes,
} from '@app/core/models';

@Injectable()
export class NotificationsService extends ApiService {

    private environmentsUrl: string;
    private notifGroupsUrl: string;

    private options = {
        headers: this.defaultHeaders,
        observe: 'body' as 'body',
    };

    constructor(httpClient: HttpClient) {
        super(httpClient);

        if (!env.api.endpoint) {
            throw new Error('Could not initialize the "notifications" service, the proxyPath configuration is invalid.');
        }

        this.environmentsUrl = `${env.api.endpoint}/Environments`;
        this.notifGroupsUrl = `${env.api.endpoint}/NotificationGroups`;
    }

    /**
     * Get the list of notification groups.
     * @param environmentId Internal identifier of the environment (optional).
     * @returns Observable of IOcbNotificationGroup[].
     */
    public getNotificationGroups(environmentId?: string): Observable<IOcbNotificationGroup[]> {
        const url = environmentId ? `${this.notifGroupsUrl}?EnvironmentId=${environmentId}` : `${this.notifGroupsUrl}`;
        return this.httpClient.get<IOcbNotificationGroup[]>(url, this.options);
    }

    /**
     * Add a notification group.
     * @param notificationGroup Notification group to create.
     * @returns Observable of IOcbNotificationGroup.
     */
    public addNotificationGroup(notificationGroup: IOcbCUNotificationGroup): Observable<IOcbNotificationGroup> {
        const url = `${this.notifGroupsUrl}`;
        return this.httpClient.post<IOcbNotificationGroup>(url, notificationGroup, this.options);
    }

    /**
     * Update an environment notification group.
     * @param notificationGroupId Internal identifier of the notification group.
     * @param notificationGroup Notification group data.
     * @returns Empty Observable.
     */
    public updateNotificationGroup(notificationGroupId: string, notificationGroup: IOcbCUNotificationGroup): Observable<void> {
        const url = `${this.notifGroupsUrl}/${notificationGroupId}`;
        return this.httpClient.put<void>(url, notificationGroup, this.options);
    }

    /**
     * Update an environment notification group.
     * @param notificationGroupId Internal identifier of the notification group.
     * @returns Empty Observable.
     */
    public deleteNotificationGroup(notificationGroupId: string): Observable<void> {
        const url = `${this.notifGroupsUrl}/${notificationGroupId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    /**
     * Get the list of recipients of an environment.
     * @param environmentId Internal identifier of the environment
     * @returns Observable of IOcbEnvironmentRecipient[].
     */
    public getEnvironmentRecipients(environmentId: string): Observable<IOcbEnvironmentRecipient[]> {
        const url = `${this.environmentsUrl}/${environmentId}/Recipients`;
        return this.httpClient.get<IOcbEnvironmentRecipient[]>(url, this.options).pipe(
            map(recipients => recipients.map(rec => this.mapRecipientLogTypesToEnums(rec))),
        );
    }

    /**
     * Get a recipient of an environment.
     * @param environmentId Internal identifier of the environment.
     * @param recipientId Internal identifier of the recipient.
     * @returns Observable of T. T depends on the 'observe' parameter.
     */
    public getEnvironmentRecipient(environmentId: string, recipientId: string, observe = 'body', reportProgress = false): Observable<IOcbEnvironmentRecipient> {
        const url = `${this.environmentsUrl}/${environmentId}/Recipients/${recipientId}`;
        return this.httpClient.get<IOcbEnvironmentRecipient>(url, this.options).pipe(
            map(recipient => this.mapRecipientLogTypesToEnums(recipient)),
        );
    }

    /**
     * Create a recipient for an environment.
     * @param environmentId Internal identifier of the environment.
     * @param recipient Recipient to create.
     * @returns Observable of T. T depends on the 'observe' parameter.
     */
    public addEnvironmentRecipient(environmentId: string, recipient: IOcbCUEnvironmentRecipient): Observable<IOcbEnvironmentRecipient> {
        const url = `${this.environmentsUrl}/${environmentId}/Recipients`;

        recipient = this.mapRecipientLogTypesToNumbers(recipient);
        return this.httpClient.post<IOcbEnvironmentRecipient>(url, recipient, this.options).pipe(
            map(newRecipient => this.mapRecipientLogTypesToEnums(newRecipient)),
        );
    }

    /**
     * Update a recipient.
     * @param environmentId Internal identifier of the environment.
     * @param recipientId Internal identifier of the recipient to delete.
     * @param recipient New recipient’s settings.
     * @returns Empty Observable.
     */
    public updateEnvironmentRecipient(environmentId: string, recipientId: string, recipient: IOcbCUEnvironmentRecipient): Observable<void> {
        const url = `${this.environmentsUrl}/${environmentId}/Recipients/${recipientId}`;

        recipient = this.mapRecipientLogTypesToNumbers(recipient);
        return this.httpClient.put<void>(url, recipient, this.options);
    }

    /**
     * Delete a recipient of an environment.
     * @param environmentId Internal identifier of the environment.
     * @param recipientId Internal identifier of the recipient to delete.
     * @returns Empty Observable.
     */
    public deleteEnvironmentRecipient(environmentId: string, recipientId: string): Observable<void> {
        const url = `${this.environmentsUrl}/${environmentId}/Recipients/${recipientId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    /**
     * Map environment recipients enum numbers to enum arrays.
     */
    private mapRecipientLogTypesToEnums(recipient: IOcbEnvironmentRecipient): IOcbEnvironmentRecipient {
        recipient.environmentLogTypesEnums = this.splitEnums(recipient.environmentLogTypes, OcbEnvironmentLogTypes);
        recipient.fileTransferLogTypesEnums = this.splitEnums(recipient.fileTransferLogTypes, OcbFileTransferLogTypes);
        recipient.remotePlatformLogTypesEnums = this.splitEnums(recipient.remotePlatformLogTypes, OcbRemotePlatformLogTypes);
        return recipient;
    }

    /**
     * Map environment recipients enum arrays to enum numbers.
     */
    private mapRecipientLogTypesToNumbers(recipient: IOcbCUEnvironmentRecipient): IOcbCUEnvironmentRecipient {
        recipient.environmentLogTypes = this.combineEnums(recipient.environmentLogTypesEnums, OcbEnvironmentLogTypes);
        recipient.fileTransferLogTypes = this.combineEnums(recipient.fileTransferLogTypesEnums, OcbFileTransferLogTypes);
        recipient.remotePlatformLogTypes = this.combineEnums(recipient.remotePlatformLogTypesEnums, OcbRemotePlatformLogTypes);
        return recipient;
    }
}
